<template>
   <section class="flex h-full grow items-center bg-brand-dark text-center text-white">
      <div class="container flex max-w-lg flex-col items-center justify-center">
         <!-- Initial intro view -->
         <template v-if="state.currentView === ResumeView.intro">
            <p class="text-regular mt-4">
               {{ $t("survey.resume.start_public_flow.paragraph_1") }}
            </p>
            <p class="text-regular mt-4">
               {{ $t("survey.resume.start_public_flow.paragraph_2") }}
            </p>
            <DefaultButton
               class="mt-10"
               size="sm"
               data-cy="start-survey"
               :color="Theme.White"
               :text="$t('survey.resume.start_public_flow.button_start_survey')"
               @click="startNewPublicFlow"
            />
            <!-- If participationResetConfirmed is true, this means the flow has been reset, and the user has been informed -->
            <!-- In this case, we need to start a new flow, and hide the (possibly confusing) resume options -->
            <p v-if="!participationResetConfirmed" data-cy="resume-link" class="text-regular mt-20 inline-block">
               {{ $t("survey.resume.start_public_flow.resume_message") }}
               <DefaultButton
                  data-cy="resume-btn"
                  style-type="text-only"
                  class="text-bolder mx-1 inline-block text-white underline-offset-8"
                  @click="showResumeView"
               >
                  {{ $t("survey.resume.start_public_flow.resume_here") }}
               </DefaultButton>
            </p>
         </template>
         <!-- Start view - presents resume code -->
         <template v-else-if="state.currentView === ResumeView.startFlow">
            <p class="text-regular mt-4">
               {{ $t("survey.resume.start_public_flow.paragraph_1") }}
            </p>
            <p class="text-regular mt-4">
               {{ $t("survey.resume.start_public_flow.paragraph_2") }}
            </p>
            <h2 class="mt-14 text-xl lg:text-3xl">
               {{ $t("survey.resume.start_public_flow.heading") }}
            </h2>
            <div data-cy="resume-code" class="mt-6 w-60 rounded-xl border-2 border-white px-8 py-3 text-white">
               {{ state.resumeCode.output }}
            </div>
            <LoadingButton
               class="mt-10"
               data-cy="continue-start-survey"
               size="sm"
               :color="Theme.White"
               :is-loading="state.isLoading"
               :text="$t('survey.button_continue')"
               @click="$emit('go-to-first-block', true)"
            />
            <p v-if="!participationResetConfirmed" data-cy="resume-link" class="text-regular mt-20 inline-block">
               {{ $t("survey.resume.start_public_flow.resume_message") }}
               <DefaultButton
                  data-cy="resume-btn"
                  style-type="text-only"
                  class="text-bolder mx-1 inline-block text-white underline-offset-8"
                  @click="showResumeView"
               >
                  {{ $t("survey.resume.start_public_flow.resume_here") }}
               </DefaultButton>
            </p>
         </template>
         <!-- Resume view - to enter code -->
         <template v-else>
            <p class="text-regular">{{ $t("survey.resume.resume_public_flow.paragraph") }}</p>
            <h2 class="mt-14 max-w-sm text-xl lg:text-3xl">
               {{ $t("survey.resume.resume_public_flow.heading") }}
            </h2>
            <CodeInput
               id="resume-code"
               class="mt-6 w-60"
               :label="$t('survey.resume.resume_code')"
               aria-only-label
               :code="state.resumeCode.input"
               name="resume-code"
               is-required
               :validation-error="state.errorOnResume"
               :show-errors="state.resumeCode.showErrors"
               @input-update="processResumeCode"
            />
            <LoadingButton
               class="mt-10"
               data-cy="resume-public-flow-btn"
               size="md"
               :color="Theme.White"
               :is-loading="state.isLoading"
               :text="$t('survey.resume.button_confirm')"
               @click="onResume"
            />
            <p class="text-regular mt-20 inline-block">
               {{ $t("survey.resume.resume_public_flow.start_new_message") }}
               <DefaultButton
                  data-cy="start-public-flow"
                  style-type="text-only"
                  class="text-bolder mx-1 inline-block text-white underline-offset-8"
                  @click="showStart1View"
               >
                  {{ $t("survey.resume.resume_public_flow.start_here") }}
               </DefaultButton>
            </p>
         </template>
      </div>
   </section>
</template>

<script setup lang="ts">
import { Theme } from "@/types";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useFlowParticipationStore } from "@/stores/flowParticipationStore";

interface Props {
   userBlockIdHistory: any;
   participationResetConfirmed?: boolean;
}

const enum ResumeView {
   intro,
   startFlow,
   resumeFlow,
}

// declarations
// -----------------------------------------
const $emit = defineEmits([
   "go-to-first-block",
   "go-to-last-block",
   "update-user-block-id-history",
   "public-resume-show-participation-reset-message",
]);
const { t: $t } = useI18n();
const $router = useRouter();
const $route = useRoute();
const flowParticipationStore = useFlowParticipationStore();

const props = defineProps<Props>();

// state
const state = reactive({
   resumeCode: {
      input: "",
      output: "",
      isValid: false,
      showErrors: false,
   },
   errorOnResume: null,
   isLoading: false,
   currentView: ResumeView.intro,
});

// Computed
// ------------------------------
const flowId = computed(() => $route.params?.id as string);

// Methods
// -----------RESUME-------------
function processResumeCode(codeInput: { value: string; isValid: boolean }) {
   state.resumeCode.isValid = codeInput.isValid;
   if (codeInput.isValid) {
      state.resumeCode.input = codeInput.value;
   }
}

async function onResume() {
   if (!state.resumeCode.isValid) {
      state.resumeCode.showErrors = true;
      return;
   }
   state.isLoading = true;

   try {
      // Set the flow participation object in the store, as this will be used on the FlowselPage and it's child components
      await flowParticipationStore.setFlowParticipation("resumePublicFlow", flowId.value, state.resumeCode.input);

      state.errorOnResume = null;

      const { resumeHistory } = flowParticipationStore.flowParticipation;

      if (flowParticipationStore.flowParticipationData?.showParticipationResetMessage === true) {
         // If the flow has been updated since the user last made progress, emit this information
         $emit("public-resume-show-participation-reset-message");
      } else if (resumeHistory?.length > 1) {
         $emit("update-user-block-id-history", resumeHistory);

         // wait for the userBlockIdHistory state to be updated
         await nextTick();

         $emit("go-to-last-block");
      } else {
         $emit("go-to-first-block");
      }
   } catch (error: any) {
      if (error.response?.data?.error_code === "locale_does_not_match_set_flow_locale") {
         $router.push({
            name: "flow",
            query: $route.query,
            params: { ...$route.params, locale: error.response?.data?.data?.localeId },
         });
         return;
      } else if (error.response.data.error_code === "flow_not_public") {
         state.errorOnResume = { validator: "error", message: $t("validation.flow.flow_not_public") };
         state.resumeCode.showErrors = true;
      } else if (error.response?.data.error_code === "resume_code_invalid") {
         state.errorOnResume = { validator: "invalid", message: $t("validation.code.invalid") };
         state.resumeCode.showErrors = true;
      } else {
         console.error("Error resuming public flow:", error);
      }
   } finally {
      state.isLoading = false;
   }
}

function showResumeView() {
   state.currentView = ResumeView.resumeFlow;
}

function showStart1View() {
   state.currentView = ResumeView.intro;
}

// ----- START -----
// Save the flow participation object in the store and show the resume code
async function startNewPublicFlow() {
   try {
      // Set the flow participation object in the store, as this will be used on the FlowselPage and it's child components
      await flowParticipationStore.setFlowParticipation("startPublicFlow", flowId.value);
      state.resumeCode.output = flowParticipationStore.flowParticipationData.resumeCode;
   } catch (error) {
      console.error("Error while starting a new public flow: ", error);
   }

   state.currentView = ResumeView.startFlow;
}
</script>
